<script setup lang="ts">
  import { ref, onMounted, computed, watch, nextTick } from 'vue'
  import { useRouter } from 'vue-router'
  import { WordDocumentTemplatesService, PlaceholdersService } from '@/types/generated/services.gen'
  import { usePDF, VuePDF } from '@tato30/vue-pdf'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import GenericPdfNav from '@/components/pdf/genericPdfNav.vue'
  // No need to import mock data
  import {
    type TWordDocumentTemplate as TemplateType,
    type PatchedTWordDocumentTemplateRequest as PatchedTemplateRequest,
    WordTemplateStatus,
    type SuperUserWordDocumentTemplate
  } from '@/types/generated/types.gen'
  import { ROUTES } from '@/router/routes'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import FileField from '@/components/admin/form/FileField.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { DownloadIcon, FileTextIcon, EllipsisVerticalIcon } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import Switch from '@/components/ui/switch/Switch.vue'
  import * as yup from 'yup'
  import { useToast } from '@/components/ui/toast'

  const router = useRouter()
  const { t } = useTranslation({
    en: {
      alreadyApproved: 'Already Approved',
      name: 'Name',
      description: 'Description',
      systemPrompt: 'System Prompt',
      document: 'Document',
      generatePlaceholders: 'Generate Placeholders',
      download: 'Download',
      downloadOriginal: 'Download Original',
      downloadGenerated: 'Download Generated',
      noDocxTemplate: 'No DOCX template available',
      noAiDocxTemplate: 'No AI-generated template available',
      wordTemplates: 'Word Templates',
      placeholdersGenerated: 'Template with placeholders has been generated',
      templateDetails: 'Template Details',
      acceptTemplate: 'Accept Template',
      acceptTemplateConfirmation:
        'Are you sure you want to accept this template? This will mark it as reviewed and make it available for use.',
      cancel: 'Cancel',
      accept: 'Accept',
      underReview: 'Under Review',
      save: 'Approve Template',
      saveChanges: 'Approve Template',
      noDescription: 'No description provided',
      uploadDocument: 'Upload Document',
      replaceDocument: 'Replace Document',
      preview: 'Preview',
      preparingPreview: 'Preparing preview...',
      showPlaceholders: 'Show Empty Placeholders',
      fillWithData: 'Fill with Sample Data',
      useOriginalTemplate: 'Use Original Template',
      useAiTemplate: 'Use AI Template'
    },
    de: {
      alreadyApproved: 'Bereits genehmigt',
      name: 'Name',
      description: 'Beschreibung',
      systemPrompt: 'System-Prompt',
      document: 'Dokument',
      generatePlaceholders: 'Platzhalter generieren',
      download: 'Herunterladen',
      downloadOriginal: 'Original herunterladen',
      downloadGenerated: 'Generierte Version herunterladen',
      noDocxTemplate: 'Keine DOCX-Vorlage verfügbar',
      noAiDocxTemplate: 'Keine KI-generierte Vorlage verfügbar',
      wordTemplates: 'Word Vorlagen',
      placeholdersGenerated: 'Vorlage mit Platzhaltern wurde generiert',
      templateDetails: 'Vorlagendetails',
      acceptTemplate: 'Vorlage akzeptieren',
      acceptTemplateConfirmation:
        'Sind Sie sicher, dass Sie diese Vorlage akzeptieren möchten? Dies markiert sie als überprüft und macht sie zur Verwendung verfügbar.',
      cancel: 'Abbrechen',
      accept: 'Akzeptieren',
      underReview: 'Wird überprüft',
      save: 'Vorlage genehmigen',
      saveChanges: 'Vorlage genehmigen',
      noDescription: 'Keine Beschreibung vorhanden',
      uploadDocument: 'Dokument hochladen',
      replaceDocument: 'Dokument ersetzen',
      preview: 'Vorschau',
      preparingPreview: 'Vorschau wird vorbereitet...',
      showPlaceholders: 'Leere Platzhalter anzeigen',
      fillWithData: 'Mit Beispieldaten füllen',
      useOriginalTemplate: 'Original-Vorlage verwenden',
      useAiTemplate: 'KI-Vorlage verwenden'
    }
  })

  const { toast } = useToast()
  const template = ref<SuperUserWordDocumentTemplate | null>(null)
  const placeholders = ref<any[]>([])
  const isGenerating = ref(false)
  const showLoadingDialog = ref(false)
  const isSaving = ref(false)
  const showAcceptModal = ref(false)
  const uploadedFile = ref<File | null>(null)
  const fileFieldKey = ref(0)
  const docxTemplateCleanUrl = computed(() => {
    if (!template.value?.docx_template) return ''
    return template.value.docx_template.split('/word_document_templates/')[1]?.split('?')[0]
  })

  // PDF viewer related data
  const arrayBuffer = ref<Uint8Array | null>(null)
  const { pdf, pages } = usePDF(arrayBuffer)
  const pdfLoaded = ref<boolean>(false)
  const currentPage = ref<number>(1)
  const isPreparing = ref<boolean>(false)
  const highlightTexts = computed(() => {
    // Always highlight the placeholder syntax
    return placeholderValues.value.map((item) => `{{${item.placeholder}}}`)
  })

  // Open the confirmation modal
  const openAcceptModal = () => {
    showAcceptModal.value = true
  }

  // Handle the template acceptance
  const handleSave = async () => {
    try {
      if (!template.value) return
      isSaving.value = true
      showAcceptModal.value = false

      // Check if we have a new file to upload
      if (uploadedFile.value) {
        // Create form data for file upload
        const fileFormData = new FormData()
        fileFormData.append('docx_template', uploadedFile.value)
        fileFormData.append('status', WordTemplateStatus.APPROVED)

        // Update the template and get the response in a single call
        const updatedTemplate =
          (await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
            uuid: template.value.uuid || '',
            formData: fileFormData as unknown as PatchedTemplateRequest
          })) as SuperUserWordDocumentTemplate

        // Update the local template with the response data directly
        template.value = updatedTemplate
      } else {
        // No file to upload, just update the status
        const dataToSave: PatchedTemplateRequest = {
          status: WordTemplateStatus.APPROVED
        }

        // Update the template and get the response in a single call
        const updatedTemplate =
          (await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
            uuid: template.value.uuid || '',
            formData: dataToSave
          })) as SuperUserWordDocumentTemplate

        // Update the local template with the response data directly
        template.value = updatedTemplate
      }

      saveSuccessToast()

      // Reset the file input
      uploadedFile.value = null
    } catch (error) {
      console.error('Error approving template:', error)
      genericErrorToast()
    } finally {
      isSaving.value = false
    }
  }

  const onPdfLoaded = (eventData: any) => {
    pdfLoaded.value = true
    isPreparing.value = false
  }

  const setPage = (page: number) => {
    currentPage.value = page
  }

  // Toggle between using original template and AI-generated template
  const useAiTemplate = ref(false)

  const placeholderValues = computed(() => {
    if (template.value?.replaceable_variables) {
      try {
        // Parse the replaceable_variables if it's a string
        const parsed =
          typeof template.value.replaceable_variables === 'string'
            ? JSON.parse(template.value.replaceable_variables)
            : template.value.replaceable_variables

        if (Array.isArray(parsed)) {
          const placeholdersEntry = parsed.find(
            (entry: any[]) => Array.isArray(entry) && entry[0] === 'placeholders'
          )

          if (placeholdersEntry && Array.isArray(placeholdersEntry[1])) {
            // Extract the placeholders from the nested structure
            const placeholders = placeholdersEntry[1].map((placeholderItem: any[]) => {
              // Each placeholder item is an array of arrays
              // Find the 'placeholder_variable' entry
              const placeholderVar = placeholderItem.find(
                (item: any[]) => Array.isArray(item) && item[0] === 'placeholder_variable'
              )

              if (placeholderVar && placeholderVar[1]) {
                const placeholderName = placeholderVar[1]
                return {
                  placeholder: placeholderName,
                  value: `{{${placeholderName}}}`
                }
              }
              return null
            })

            // Filter out null values and cast to the expected type
            return placeholders.filter(
              (item): item is { placeholder: string; value: string } => item !== null
            )
          }
        }

        console.log('Could not extract placeholders from structure:', parsed)
      } catch (error) {
        console.error('Error processing replaceable_variables:', error)
      }
    }

    // Fallback to minimal default placeholders if none found in template
    return [{ placeholder: 'placeholder', value: 'placeholder' }]
  })

  // No mock data mapping needed

  const getPlaceholderValues = () => {
    // Always use the placeholder name with {{}} syntax
    console.log('Sending placeholder values with {{}}:', placeholderValues.value)
    return placeholderValues.value
  }

  const fetchPreviewPdf = async () => {
    try {
      if (!template.value || !template.value.uuid) {
        console.error('No template available')
        return
      }

      isPreparing.value = true
      pdfLoaded.value = false

      // First, ensure we have the latest template data
      const latestTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid
      })) as SuperUserWordDocumentTemplate

      // Update the local template with the latest data
      template.value = latestTemplate

      // Check if we have the appropriate template based on user selection
      if (useAiTemplate.value && !latestTemplate.ai_docx_template) {
        console.error('No AI-generated template available')
        isPreparing.value = false
        return
      } else if (!useAiTemplate.value && !latestTemplate.docx_template) {
        console.error('No original DOCX template available')
        isPreparing.value = false
        return
      }

      const placeholderValues = getPlaceholderValues()

      const pdfBlob =
        await WordDocumentTemplatesService.wordDocumentTemplatesCreateWordDocumentPreviewBlobCreate(
          {
            uuid: template.value.uuid || '',
            requestBody: {
              word_document_template_uuid: template.value.uuid || '',
              placeholder_values: placeholderValues,
              use_ai_template: useAiTemplate.value
            }
          }
        )

      // Convert blob to ArrayBuffer
      const data = await pdfBlob.arrayBuffer()
      arrayBuffer.value = new Uint8Array(data)

      // Reset to first page
      currentPage.value = 1

      // Force the PDF to load if it doesn't trigger automatically
      setTimeout(() => {
        if (!pdfLoaded.value) {
          pdfLoaded.value = true
          isPreparing.value = false
        }
      }, 3000)
    } catch (error: any) {
      console.error('Error generating PDF preview:', error.body, error)
      toast({
        title: 'Generation Error',
        description: error.body,
        variant: 'destructive',
        duration: 15000
      })
      isPreparing.value = false
      pdfLoaded.value = false
    }
  }

  const handleFileUpload = async () => {
    try {
      if (!template.value || !uploadedFile.value) return
      isPreparing.value = true

      const formData: any = {
        name: template.value.name,
        description: template.value.description,
        system_prompt: template.value.system_prompt,
        law_field: template.value.law_field,
        law_topic: template.value.law_topic,
        docx_template: uploadedFile.value
      }

      await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
        uuid: template.value.uuid || '',
        formData: formData
      })

      const updatedTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid || ''
      })) as SuperUserWordDocumentTemplate

      template.value = updatedTemplate
      uploadedFile.value = null
      fileFieldKey.value++
      await fetchPreviewPdf()
    } catch (error) {
      console.error('Error handling file upload:', error)
      genericErrorToast()
      uploadedFile.value = null
      fileFieldKey.value++
      isPreparing.value = false
    }
  }

  const downloadDOCX = async (useAiVersion = false) => {
    try {
      // Determine which template URL to use
      const templateUrl = useAiVersion
        ? template.value?.ai_docx_template
        : template.value?.docx_template

      if (!templateUrl) {
        genericErrorToast()
        console.error(`No ${useAiVersion ? 'AI-generated' : 'original'} template URL available`)
        return
      }

      const response = await fetch(templateUrl)

      if (!response.ok) {
        console.error(`Error downloading file: ${response.status} ${response.statusText}`)
        genericErrorToast()
        return
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${template.value?.name}${useAiVersion ? '_with_placeholders' : ''}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error in downloadDOCX:', error)
      genericErrorToast()
    }
  }

  const generatePlaceholders = async () => {
    try {
      isGenerating.value = true
      showLoadingDialog.value = true

      if (!template.value?.docx_template) {
        genericErrorToast()
        console.error('No docx_template URL available')
        showLoadingDialog.value = false
        return
      }

      const response =
        await WordDocumentTemplatesService.wordDocumentTemplatesCreateWordDocumentTemplateDocxWithPlaceholdersCreate(
          {
            uuid: template.value.uuid || '',
            requestBody: {
              word_document_template_uuid: template.value.uuid || ''
            }
          }
        )

      if (response && typeof response === 'object' && 'success' in response && response.success) {
        const updatedTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
          uuid: template.value.uuid || ''
        })) as SuperUserWordDocumentTemplate

        template.value = updatedTemplate
        useAiTemplate.value = true
        fetchPreviewPdf()
        saveSuccessToast()
      } else {
        console.error('Invalid response format or operation failed:', response)
        genericErrorToast()
      }
    } catch (error) {
      console.error('Error generating template with placeholders:', error)
      genericErrorToast()
    } finally {
      isGenerating.value = false
      showLoadingDialog.value = false
    }
  }

  // Watch for template changes to load PDF preview
  watch(
    () => template.value?.docx_template,
    (newDocxTemplate) => {
      if (newDocxTemplate) {
        nextTick(() => {
          fetchPreviewPdf()
        })
      }
    },
    { immediate: true }
  )

  // Watch for pdf changes to ensure PDF loads properly
  watch(
    () => pdf.value,
    (newPdf) => {
      if (newPdf && !pdfLoaded.value) {
        pdfLoaded.value = true
        isPreparing.value = false
      }
    }
  )

  onMounted(async () => {
    try {
      template.value = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: router.currentRoute.value.params.uuid.toString()
      })) as SuperUserWordDocumentTemplate

      // Load available placeholders
      placeholders.value = await PlaceholdersService.placeholdersList()
    } catch (error) {
      genericErrorToast()
    }
  })
</script>

<template>
  <div
    v-if="template"
    class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5"
  >
    <!-- Page Header -->
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: t('wordTemplates'), to: ROUTES.ADMIN.WORD_TEMPLATES.REVIEW },
          {
            title: template.name,
            to: ROUTES.ADMIN.WORD_TEMPLATES.REVIEW_DETAIL.replace(':uuid', template.uuid || '')
          }
        ]"
      />

      <div class="flex items-center justify-end gap-x-3">
        <DropdownMenu v-if="template.docx_template">
          <DropdownMenuTrigger as-child>
            <Button
              variant="ghost"
              class="flex h-9 w-9 p-0 data-[state=open]:bg-muted"
            >
              <EllipsisVerticalIcon class="h-5 w-5" />
              <span class="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            class="w-[200px]"
          >
            <DropdownMenuItem
              v-if="template?.docx_template"
              class="cursor-pointer"
              @click="downloadDOCX(false)"
            >
              <DownloadIcon class="mr-2 h-4 w-4" />
              {{ t('downloadOriginal') }}
            </DropdownMenuItem>
            <DropdownMenuItem
              v-if="template?.ai_docx_template"
              class="cursor-pointer"
              @click="downloadDOCX(true)"
            >
              <DownloadIcon class="mr-2 h-4 w-4" />
              {{ t('downloadGenerated') }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="outline"
          class="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black shadow-sm
            hover:bg-gray-100 hover:text-gray-900"
          :disabled="isGenerating"
          @click="generatePlaceholders"
        >
          <FileTextIcon class="mr-2 h-4 w-4" />
          {{ t('generatePlaceholders') }}
        </Button>
        <Button
          v-if="template.status !== WordTemplateStatus.APPROVED"
          variant="default"
          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-4 py-2
            text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
            focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
          :disabled="isSaving"
          @click="openAcceptModal"
        >
          <div class="flex items-center">
            <span>{{ t('saveChanges') }}</span>
          </div>
        </Button>
        <Button
          v-else
          variant="outline"
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
            disabled:pointer-events-none disabled:opacity-50"
          disabled
        >
          <div class="flex items-center">
            <span>{{ t('alreadyApproved') }}</span>
          </div>
        </Button>
      </div>
    </div>

    <div class="flex flex-col space-y-5 md:flex-row md:gap-5 md:space-y-0">
      <!-- Template Details Section - 30% width -->
      <div
        class="w-3/10 rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-xl font-semibold">{{ t('templateDetails') }}</h2>
          <!-- Status badge -->
          <div
            v-if="template.status === WordTemplateStatus.APPROVED"
            class="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800"
          >
            Approved
          </div>
          <div
            v-else-if="template.status === WordTemplateStatus.IN_REVIEW"
            class="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800"
          >
            {{ t('underReview') }}
          </div>
        </div>

        <!-- Template Details -->
        <div class="w-full">
          <div class="mb-4">
            <InputField
              v-model="template.name"
              name="name"
              label="Name"
              :rules="yup.string().required()"
              :disabled="true"
            />
            <InputField
              v-model="template.law_field"
              name="law_field"
              label="Rechtsgebiet"
              :disabled="true"
            />
            <InputField
              v-model="template.law_topic"
              name="law_topic"
              title="Rechtsthema"
              label="Rechtsthema"
              :disabled="true"
            />
            <TextAreaField
              v-model="template.description"
              name="description"
              label="Beschreibung"
              :disabled="true"
            />
          </div>

          <!-- Display current document if it exists -->
          <div
            v-if="template.docx_template"
            class="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
          >
            <div class="flex items-center justify-between gap-2">
              <div>
                <h3 class="truncate text-sm font-medium text-gray-900">
                  {{ docxTemplateCleanUrl }}
                </h3>
              </div>
              <div class="flex gap-2">
                <a
                  :href="template.docx_template"
                  download
                  class="inline-flex items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900
                    shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <DownloadIcon class="h-5 w-5 text-gray-500" />
                  {{ t('download') }}
                </a>
              </div>
            </div>
          </div>

          <!-- File upload field -->
          <div class="mb-4">
            <label class="mb-2 block text-sm font-medium text-gray-800">{{ t('document') }}</label>
            <FileField
              :key="fileFieldKey"
              v-model="uploadedFile"
              name="docx_template"
              :label="template.docx_template ? t('replaceDocument') : t('uploadDocument')"
              :allowed-file-extensions="['docx']"
              @update:model-value="handleFileUpload"
            />
          </div>
        </div>
      </div>

      <!-- PDF Preview Section - 70% width -->
      <div
        v-if="template && template.docx_template"
        class="md:w-7/10 w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="mb-4 flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold">{{ t('document') }} {{ t('preview') }}</h2>
          </div>

          <!-- Toggle for template type - only shown if AI template exists -->
          <div
            v-if="template?.ai_docx_template"
            class="mt-2 flex items-center justify-between"
          >
            <h3 class="text-sm font-medium text-gray-700">
              {{ t('document') }}
            </h3>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-600">{{ t('useAiTemplate') }}</span>
              <Switch
                :checked="useAiTemplate"
                class="data-[state=checked]:bg-purple-500 data-[state=unchecked]:bg-gray-200"
                @update:checked="
                  (value) => {
                    useAiTemplate = value
                    fetchPreviewPdf()
                  }
                "
              />
            </div>
          </div>
        </div>

        <!-- PDF viewer container -->
        <div class="w-full flex-1 overflow-auto rounded-lg border border-gray-200">
          <div class="relative h-full flex-1">
            <div v-if="pdfLoaded && pdf">
              <div class="flex h-full items-center justify-center">
                <div class="pdf-container">
                  <VuePDF
                    ref="vuePDFRef"
                    :pdf="pdf"
                    class="pdf"
                    on-layer
                    text-layer
                    :scale="1.2"
                    :page="currentPage || 1"
                    :highlight-text="highlightTexts"
                    :highlight-options="{
                      completeWords: false,
                      ignoreCase: false
                    }"
                    @loaded="onPdfLoaded"
                    @text-loaded="onPdfLoaded"
                  />
                </div>
              </div>
            </div>
            <div
              v-else
              class="overflow-hidden"
            >
              <div>
                <SkeletonLoader
                  :width="'100%'"
                  :height="'100%'"
                  class="flex min-h-[600px] items-center justify-center bg-gray-100"
                >
                  <div
                    v-if="isPreparing"
                    class="text-center"
                  >
                    <div
                      class="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-b-2 border-purple-700"
                    ></div>
                    <p class="text-gray-600">{{ t('preparingPreview') }}</p>
                  </div>
                </SkeletonLoader>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-3 flex items-center justify-between">
          <GenericPdfNav
            :current-page="currentPage || 1"
            :total-pages="pages || 1"
            @update-page="setPage"
          />
        </div>
      </div>
    </div>

    <!-- Document Preview is now placed next to the template details in a 30-70 layout -->
    <!-- See the updated layout above -->
  </div>

  <!-- Accept Template Confirmation Modal -->
  <Dialog
    :open="showAcceptModal"
    @update:open="showAcceptModal = $event"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('acceptTemplate') }}</DialogTitle>
        <DialogDescription>
          {{ t('acceptTemplateConfirmation') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-row justify-end gap-2 sm:justify-end">
        <Button
          variant="outline"
          @click="showAcceptModal = false"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="default"
          class="bg-purple-600 hover:bg-purple-700"
          :disabled="isSaving"
          @click="handleSave"
        >
          <div class="flex items-center">
            <span>{{ t('accept') }}</span>
            <span
              v-if="isSaving"
              class="ml-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"
            ></span>
          </div>
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  <!-- Loading Dialog -->
  <Dialog
    :open="showLoadingDialog"
    :modal="true"
  >
    <DialogContent class="sm:max-w-md">
      <div class="flex flex-col items-center justify-center space-y-4 p-6">
        <div
          class="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent"
        ></div>
        <p class="text-lg font-medium">{{ $t('Generating document with placeholders...') }}</p>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
  /* Custom styling for highlighted text in PDF */
  :deep(.highlight) {
    background-color: rgba(69, 154, 252, 0.3); /* Blue highlight with transparency */
    border: 2px dashed rgba(69, 154, 252, 0.3); /* Light blue dashed border */
    border-radius: 2px;
    padding: 0;
    margin: -2 -1;
    box-shadow: 0 0 4px rgba(69, 154, 252, 0.5);
  }

  .pdf-container {
    max-width: 100%;
    overflow: auto;
  }

  /* Custom width classes for the 30-70 layout */
  @media (min-width: 768px) {
    .md\:w-3\/10 {
      width: 30%;
    }

    .md\:w-7\/10 {
      width: 70%;
    }
  }
</style>
